// La app de iOS y Android (movil/, Capacitor).
//
// El portal es la misma página en el navegador y dentro de la app «Agentical e-CF». La app inyecta antes de
// arrancar un guion propio que publica `window.__DEVTEK_MOVIL__`: la cámara, compartir, la sesión
// guardada tras la huella y los avisos. Aquí se pregunta si está, como `pos/src/lib/escritorio.ts`
// pregunta por la caja de escritorio; en el navegador no está y nada de esto se usa.
//
// No se importa `@capacitor/core`: los paquetes de Capacitor viven en `movil/package.json` y el
// portal del navegador no carga ni un byte de ellos. Este archivo es la vista del portal sobre el
// contrato de `movil/puente/contrato.ts`; `npm run comprobar` en movil/ falla si dejan de casar.
// Por eso no importa nada con `@/`: lo compila también movil/.

export interface PuenteMovilPortal {
  readonly app: 'portal'
  readonly plataforma: 'ios' | 'android'
  readonly version: string
  readonly servicio: string
  abrirFuera(url: string): Promise<void>
  compartir: {
    archivo(archivo: Blob, nombre: string, titulo?: string): Promise<'compartido' | 'cancelado'>
    enlace(url: string, texto?: string, titulo?: string): Promise<'compartido' | 'cancelado'>
  }
  camara: {
    tomarFoto(proposito: 'documento' | 'articulo'): Promise<File | null>
  }
  sesionGuardada: {
    consultar(): Promise<{ vence: string; email: string } | null>
    abrir(): Promise<string | null>
    ofrecer(datos: string, vence: string, email: string): Promise<boolean>
    olvidar(): Promise<void>
  }
  avisos: {
    readonly disponibles: boolean
    registrar(): Promise<{ token: string; proveedor: 'fcm' | 'apns' } | { motivo: 'sin_permiso' | 'no_configurado' | 'error'; detalle?: string }>
    alTocar(accion: (aviso: { ruta: string; tenant: string | null }) => void): void
    instalacion(): string
  }
}

/** El puente de la app, o `null` en el navegador (y en la app de la caja, que es otra). */
export function puenteMovil(): PuenteMovilPortal | null {
  if (typeof window === 'undefined') return null
  const puente = (window as unknown as { __DEVTEK_MOVIL__?: { app?: string } }).__DEVTEK_MOVIL__
  return puente?.app === 'portal' ? (puente as PuenteMovilPortal) : null
}

/** Si la página corre dentro de la app de iOS o Android. */
export function enMovil(): boolean {
  return puenteMovil() !== null
}

/**
 * La foto de una factura de gasto con la cámara del teléfono, para adjuntarla como cualquier
 * archivo elegido. En la app usa la cámara del sistema; en el navegador devuelve `null` y la
 * pantalla sigue con su `<input type="file" accept="image/*" capture="environment">` de siempre,
 * que en un teléfono ya abre la cámara.
 */
export async function tomarFotoDeDocumento(): Promise<File | null> {
  const movil = puenteMovil()
  return movil ? await movil.camara.tomarFoto('documento') : null
}

// Bajar un archivo no cambia: `descargarArchivo` y los demás crean su enlace `blob:` y lo pulsan,
// y en la app ese clic abre la hoja de compartir del sistema (WhatsApp, Correo, Imprimir). Lo hace
// el puente (movil/puente/comun.ts), no cada pantalla.
